/**
 * Audit period split — see docs/multi-year/multi-year-bs-pl-trigger-logic-gap.md.
 * Steps: 2 (first+cessation YE) → 1 (checkbox + 1.1/1.2) → 3 (regular anniversary).
 */
import {
    addDays,
    addMonths,
    addYears,
    computeYearOfAssessment,
    daysBetween,
    formatDDMMYYYY,
    isWithin18Months,
    parseDateFlexible,
} from '@/utils/dataUtils';

export type AuditSpanType = 'SINGLE' | 'MULTI';
export type AuditSplitMode =
    | 'REGULAR'
    | 'FIRST_AUDIT_18M' // deprecated legacy mode name; unused by new resolve
    | 'FIRST_AUDIT_YE'
    | 'CESSATION';

export type MonthDay = { day: number; month: number }; // day 1..31, month 1..12

export type AuditPeriodSlice = {
    index: number;
    periodFrom: string;
    periodTo: string;
    yearOfAssessment: string | null;
    key: string;
    label: string;
};

/** Phase 4: PL Tab / WP payload 子期间作用域 */
export type WpPeriodScope = {
    periodFrom: string;
    periodTo: string;
    yearOfAssessment?: string | null;
    sliceKey: string;
    /** MULTI: true for leftmost (earliest) Period Tab — may load shared WP API; others stay draft-isolated */
    isFirstSlice?: boolean;
};

export type ResolveAuditSpanInput = {
    periodFrom: string | null | undefined;
    periodTo: string | null | undefined;
    inCorpDate?: string | null;
    cessationDuringThisPeriod?: number | null;
    /** @deprecated former 18m Yes/No — ignored by resolve */
    firstAuditFollows18Months?: boolean | null;
    /** Step 2: user-confirmed year-end dd/MM */
    firstAuditYearEndMD?: MonthDay | string | null;
    /** Step 1 gate; null/undefined treated as true (default) */
    isMultiYearAudit?: boolean | null;
    /** @deprecated unused — cessation cutoff derived from periodFrom−1 day */
    cessationYearEndMD?: MonthDay | null;
    /** @deprecated unused by new cessation split */
    lastYearPeriodTo?: string | null;
};

export type ResolveAuditSpanResult = {
    type: AuditSpanType;
    mode: AuditSplitMode;
    slices: AuditPeriodSlice[];
    /** Step 2: need user year-end MD confirm */
    needsFirstAuditConfirm?: boolean;
};

function formatDMY(d: Date): string {
    return formatDDMMYYYY(d);
}

function sliceKey(from: string, to: string): string {
    const a = parseDateFlexible(from);
    const b = parseDateFlexible(to);
    if (!a || !b) return `${from}_${to}`;
    const f = (x: Date) =>
        `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
    return `${f(a)}_${f(b)}`;
}

export function buildSlice(periodFrom: string, periodTo: string, index: number): AuditPeriodSlice {
    const yoa = computeYearOfAssessment(periodTo);
    return {
        index,
        periodFrom,
        periodTo,
        yearOfAssessment: yoa,
        key: sliceKey(periodFrom, periodTo),
        label: yoa ? `YOA ${yoa}` : `${periodFrom} – ${periodTo}`,
    };
}

function fallbackWhole(
    periodFrom: string | null | undefined,
    periodTo: string | null | undefined,
): AuditPeriodSlice[] {
    if (periodFrom && periodTo) return [buildSlice(periodFrom, periodTo, 0)];
    return [];
}

/** True if Feb 29 of a leap year falls within [from, to] inclusive. */
export function periodContainsLeapDay(periodFrom: string, periodTo: string): boolean {
    const fromDate = parseDateFlexible(periodFrom);
    const toDate = parseDateFlexible(periodTo);
    if (!fromDate || !toDate) return false;
    const startYear = fromDate.getFullYear();
    const endYear = toDate.getFullYear();
    for (let year = startYear; year <= endYear; year++) {
        const isYearLeap = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
        if (!isYearLeap) continue;
        const feb29 = new Date(year, 1, 29);
        const fromTime = new Date(fromDate.getFullYear(), fromDate.getMonth(), fromDate.getDate()).getTime();
        const toTime = new Date(toDate.getFullYear(), toDate.getMonth(), toDate.getDate()).getTime();
        if (feb29.getTime() >= fromTime && feb29.getTime() <= toTime) return true;
    }
    return false;
}

export function resolveYearDayCount(periodFrom: string, periodTo: string): 365 | 366 {
    return periodContainsLeapDay(periodFrom, periodTo) ? 366 : 365;
}

/** Year-day count for a forward 12-month slice starting at `cursor`. */
export function resolveYearDayCountStarting(cursor: Date): 365 | 366 {
    const start = formatDMY(cursor);
    const endApprox = formatDMY(addDays(cursor, 366));
    return resolveYearDayCount(start, endApprox);
}

export function isWithinOneCalendarYear(periodFrom: string, periodTo: string): boolean {
    const days = daysBetween(periodFrom, periodTo);
    if (days == null) return true;
    return days <= resolveYearDayCount(periodFrom, periodTo);
}

/** Parse `dd/MM` or `{day,month}`. Returns null if invalid / incomplete. */
export function parseMonthDay(input?: MonthDay | string | null): MonthDay | null {
    if (!input) return null;
    if (typeof input === 'object' && typeof input.day === 'number' && typeof input.month === 'number') {
        return validateMonthDay(input.day, input.month) ? input : null;
    }
    const s = String(input).trim();
    const m = /^([0-9]{1,2})\/([0-9]{1,2})$/.exec(s);
    if (!m) return null;
    const day = Number(m[1]);
    const month = Number(m[2]);
    return validateMonthDay(day, month) ? {day, month} : null;
}

export function formatMonthDay(md: MonthDay): string {
    return `${String(md.day).padStart(2, '0')}/${String(md.month).padStart(2, '0')}`;
}

/**
 * Valid calendar month-day. Per product #10: reject 29/02 (and any impossible day).
 */
export function validateMonthDay(day: number, month: number): boolean {
    if (!Number.isInteger(day) || !Number.isInteger(month)) return false;
    if (month < 1 || month > 12) return false;
    if (day < 1) return false;
    if (month === 2) return day >= 1 && day <= 28; // never allow 29/02
    const max = [0, 31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][month];
    return day <= max;
}

export function firstYearEndOnOrAfter(cursor: Date, yearEndMD: MonthDay): Date {
    let y = cursor.getFullYear();
    let candidate = new Date(y, yearEndMD.month - 1, yearEndMD.day);
    // Normalize invalid (e.g. 31/04) — should already be validated
    if (
        candidate.getMonth() !== yearEndMD.month - 1 ||
        candidate.getDate() !== yearEndMD.day
    ) {
        candidate = new Date(y, yearEndMD.month, 0); // last day of month
    }
    const c0 = new Date(cursor.getFullYear(), cursor.getMonth(), cursor.getDate());
    if (candidate.getTime() < c0.getTime()) {
        y += 1;
        candidate = new Date(y, yearEndMD.month - 1, yearEndMD.day);
    }
    return candidate;
}

/** If first year-end is “too close” (< one calendar year), advance to next year-end. */
export function ensureMinFirstSpan(cursorStart: Date, sliceEnd: Date, yearEndMD: MonthDay): Date {
    const from = formatDMY(cursorStart);
    const to = formatDMY(sliceEnd);
    const days = daysBetween(from, to);
    // Compare against a full calendar year (365/366), not the short span's own leap-day count
    const probeEnd = addDays(cursorStart, 366);
    const minDays = resolveYearDayCount(from, formatDMY(probeEnd));
    if (days != null && days < minDays) {
        return firstYearEndOnOrAfter(addDays(sliceEnd, 1), yearEndMD);
    }
    return sliceEnd;
}

/** Derive `{day,month}` from a `dd/MM/yyyy` date; Feb 29 → 28. */
export function deriveMonthDayFromDate(dateStr?: string | null): MonthDay | null {
    const d = parseDateFlexible(dateStr);
    if (!d) return null;
    let day = d.getDate();
    const month = d.getMonth() + 1;
    if (month === 2 && day === 29) day = 28;
    return validateMonthDay(day, month) ? {day, month} : null;
}

export function splitRegularPeriods(
    periodFrom: string,
    periodTo: string,
): AuditPeriodSlice[] {
    const from = parseDateFlexible(periodFrom);
    const to = parseDateFlexible(periodTo);
    if (!from || !to || to < from) return [];

    const slices: AuditPeriodSlice[] = [];
    let cursorStart = from;

    while (cursorStart <= to) {
        const nextStart = addYears(cursorStart, 1);
        let sliceEnd = addDays(nextStart, -1);
        if (sliceEnd > to) sliceEnd = to;

        slices.push(buildSlice(formatDMY(cursorStart), formatDMY(sliceEnd), slices.length));

        cursorStart = nextStart;
        if (cursorStart > to) break;
        if (slices.length > 20) break;
    }
    return slices;
}

export function splitFirstAuditBy18Months(
    periodFrom: string,
    periodTo: string,
): AuditPeriodSlice[] {
    if (isWithin18Months(periodFrom, periodTo)) {
        return [buildSlice(periodFrom, periodTo, 0)];
    }

    const from = parseDateFlexible(periodFrom)!;
    const to = parseDateFlexible(periodTo)!;
    const slices: AuditPeriodSlice[] = [];

    let firstEnd = addDays(addMonths(from, 18), -1);
    if (firstEnd > to) firstEnd = to;
    slices.push(buildSlice(periodFrom, formatDMY(firstEnd), 0));

    let cursor = addDays(firstEnd, 1);
    while (cursor <= to) {
        const yearDays = resolveYearDayCountStarting(cursor);
        let sliceEnd = addDays(cursor, yearDays - 1);
        if (sliceEnd >= to) {
            slices.push(buildSlice(formatDMY(cursor), periodTo, slices.length));
            break;
        }
        slices.push(buildSlice(formatDMY(cursor), formatDMY(sliceEnd), slices.length));
        cursor = addDays(sliceEnd, 1);
        if (slices.length > 20) break;
    }
    return slices;
}

export function splitByYearEndMD(
    periodFrom: string,
    periodTo: string,
    yearEndMD: MonthDay,
    options: {skipShortFirstYearEnd: boolean},
): AuditPeriodSlice[] {
    const from = parseDateFlexible(periodFrom);
    const to = parseDateFlexible(periodTo);
    if (!from || !to || to < from) return [];

    const slices: AuditPeriodSlice[] = [];
    let cursorStart = from;

    while (cursorStart <= to) {
        let sliceEnd = firstYearEndOnOrAfter(cursorStart, yearEndMD);
        // Only the first slice may skip a too-close year-end (cessation path)
        if (options.skipShortFirstYearEnd && slices.length === 0) {
            sliceEnd = ensureMinFirstSpan(cursorStart, sliceEnd, yearEndMD);
        }
        if (sliceEnd >= to) {
            slices.push(buildSlice(formatDMY(cursorStart), periodTo, slices.length));
            break;
        }
        slices.push(buildSlice(formatDMY(cursorStart), formatDMY(sliceEnd), slices.length));
        cursorStart = addDays(sliceEnd, 1);
        if (slices.length > 20) break;
    }
    return slices;
}

export function splitCessationPeriods(
    periodFrom: string,
    periodTo: string,
    yearEndMD?: MonthDay | null,
): AuditPeriodSlice[] {
    if (isWithinOneCalendarYear(periodFrom, periodTo)) {
        return [buildSlice(periodFrom, periodTo, 0)];
    }
    const md = yearEndMD ?? deriveCutoffMonthDayFromPeriodFrom(periodFrom);
    if (!md) {
        return splitRegularPeriods(periodFrom, periodTo);
    }
    return splitByYearEndMD(periodFrom, periodTo, md, {skipShortFirstYearEnd: false});
}

/**
 * Step 1.2 cutoff MD = calendar day before periodFrom (e.g. 04/05 → 03/05; 01/01 → 31/12).
 */
export function deriveCutoffMonthDayFromPeriodFrom(periodFrom: string): MonthDay | null {
    const d = parseDateFlexible(periodFrom);
    if (!d) return null;
    return deriveMonthDayFromDate(formatDMY(addDays(d, -1)));
}

/** Max allowed periodTo when periodFrom === inCorpDate (inclusive). */
export function maxPeriodToForFirstAudit(periodFrom: string): string | null {
    const f = parseDateFlexible(periodFrom);
    if (!f) return null;
    return formatDMY(addMonths(f, 18));
}

export function isMultiYearAuditEnabled(flag?: boolean | null): boolean {
    return flag !== false;
}

export function splitFirstAuditPeriods(
    periodFrom: string,
    periodTo: string,
    opts: {
        follows18Months?: boolean | null;
        yearEndMD?: MonthDay | null;
    },
): AuditPeriodSlice[] {
    if (isWithinOneCalendarYear(periodFrom, periodTo)) {
        return [buildSlice(periodFrom, periodTo, 0)];
    }
    // Legacy: Yes → 18m split (kept for callers; resolve no longer uses it)
    if (opts.follows18Months === true) {
        return splitFirstAuditBy18Months(periodFrom, periodTo);
    }
    const md = opts.yearEndMD ?? deriveMonthDayFromDate(periodTo);
    if (md) {
        return splitByYearEndMD(periodFrom, periodTo, md, {
            skipShortFirstYearEnd: false,
        });
    }
    return [];
}

/**
 * Step 2: first audit + cessation, >1 year, year-end MD not yet confirmed.
 */
export function needsYearEndConfirm(input: {
    periodFrom?: string | null;
    periodTo?: string | null;
    inCorpDate?: string | null;
    cessationDuringThisPeriod?: number | null;
    firstAuditYearEndMD?: MonthDay | string | null;
}): boolean {
    const {periodFrom, periodTo, inCorpDate} = input;
    if (!periodFrom || !periodTo || !inCorpDate) return false;
    if (inCorpDate !== periodFrom) return false;
    if (input.cessationDuringThisPeriod !== 1) return false;
    if (isWithinOneCalendarYear(periodFrom, periodTo)) return false;
    return !parseMonthDay(input.firstAuditYearEndMD ?? null);
}

/** @deprecated use needsYearEndConfirm — step 2 YE dialog */
export function needsFirstAuditPeriodConfirm(input: {
    periodFrom?: string | null;
    periodTo?: string | null;
    inCorpDate?: string | null;
    cessationDuringThisPeriod?: number | null;
    firstAuditFollows18Months?: boolean | null;
    firstAuditYearEndMD?: MonthDay | string | null;
}): boolean {
    return needsYearEndConfirm(input);
}

/**
 * PL 落库唯一键 (annual_id, fs_id, year_of_assessment)。
 * REGULAR 周年拆分时，尾段常与前段落到同一 YOA（如 01/01/2024–01/01/2025 →
 * 两段都是 2024/25），必须合并，否则 MULTI 双列同 YOA 保存 Duplicate entry。
 */
export function collapseSlicesSharingYoa(slices: AuditPeriodSlice[]): AuditPeriodSlice[] {
    if (!slices || slices.length <= 1) return slices || [];
    const mergedByYoa = new Map<string, {periodFrom: string; periodTo: string}>();
    const yoaOrder: string[] = [];
    const noYoa: AuditPeriodSlice[] = [];
    for (const s of slices) {
        const yoa = String(s.yearOfAssessment || '').trim();
        if (!yoa) {
            noYoa.push(s);
            continue;
        }
        const prev = mergedByYoa.get(yoa);
        if (!prev) {
            mergedByYoa.set(yoa, {periodFrom: s.periodFrom, periodTo: s.periodTo});
            yoaOrder.push(yoa);
            continue;
        }
        const prevFrom = parseDateFlexible(prev.periodFrom);
        const prevTo = parseDateFlexible(prev.periodTo);
        const nextFrom = parseDateFlexible(s.periodFrom);
        const nextTo = parseDateFlexible(s.periodTo);
        if (nextFrom && prevFrom && nextFrom < prevFrom) prev.periodFrom = s.periodFrom;
        if (nextTo && prevTo && nextTo > prevTo) prev.periodTo = s.periodTo;
    }
    const collapsed = yoaOrder.map((yoa, i) => {
        const span = mergedByYoa.get(yoa)!;
        return buildSlice(span.periodFrom, span.periodTo, i);
    });
    if (noYoa.length === 0) return collapsed;
    return [...collapsed, ...noYoa.map((s, i) => ({...s, index: collapsed.length + i}))];
}

function resultFromSlices(
    slices: AuditPeriodSlice[],
    mode: AuditSplitMode,
): ResolveAuditSpanResult {
    const collapsed = collapseSlicesSharingYoa(slices);
    return {
        type: collapsed.length <= 1 ? 'SINGLE' : 'MULTI',
        mode,
        slices: collapsed,
    };
}

export function resolveAuditSpanType(input: ResolveAuditSpanInput): ResolveAuditSpanResult {
    const {periodFrom, periodTo} = input;
    if (!periodFrom || !periodTo) {
        return {type: 'SINGLE', mode: 'REGULAR', slices: fallbackWhole(periodFrom, periodTo)};
    }

    const from = parseDateFlexible(periodFrom);
    const to = parseDateFlexible(periodTo);
    if (!from || !to || to < from) {
        return {type: 'SINGLE', mode: 'REGULAR', slices: fallbackWhole(periodFrom, periodTo)};
    }

    const isFirstAudit = !!(input.inCorpDate && input.inCorpDate === periodFrom);
    const isCessation = input.cessationDuringThisPeriod === 1;
    const withinOneYear = isWithinOneCalendarYear(periodFrom, periodTo);

    // —— Step 2: first audit + cessation ——
    if (isFirstAudit && isCessation) {
        if (withinOneYear) {
            return {
                type: 'SINGLE',
                mode: 'FIRST_AUDIT_YE',
                slices: [buildSlice(periodFrom, periodTo, 0)],
            };
        }
        const md = parseMonthDay(input.firstAuditYearEndMD ?? null);
        if (!md) {
            return {
                type: 'SINGLE',
                mode: 'FIRST_AUDIT_YE',
                slices: [],
                needsFirstAuditConfirm: true,
            };
        }
        return resultFromSlices(
            splitByYearEndMD(periodFrom, periodTo, md, {skipShortFirstYearEnd: false}),
            'FIRST_AUDIT_YE',
        );
    }

    // —— Step 1: first audit XOR cessation ——
    if (isFirstAudit || isCessation) {
        if (withinOneYear) {
            return {
                type: 'SINGLE',
                mode: isFirstAudit ? 'FIRST_AUDIT_YE' : 'CESSATION',
                slices: [buildSlice(periodFrom, periodTo, 0)],
            };
        }
        // Checkbox gate (default true): unchecked → SINGLE whole period
        if (!isMultiYearAuditEnabled(input.isMultiYearAudit)) {
            return {
                type: 'SINGLE',
                mode: isFirstAudit ? 'FIRST_AUDIT_YE' : 'CESSATION',
                slices: [buildSlice(periodFrom, periodTo, 0)],
            };
        }
        if (isFirstAudit) {
            const md = deriveMonthDayFromDate(periodTo);
            if (!md) {
                return {
                    type: 'SINGLE',
                    mode: 'FIRST_AUDIT_YE',
                    slices: [buildSlice(periodFrom, periodTo, 0)],
                };
            }
            return resultFromSlices(
                splitByYearEndMD(periodFrom, periodTo, md, {skipShortFirstYearEnd: false}),
                'FIRST_AUDIT_YE',
            );
        }
        // 1.2 cessation: cutoff = periodFrom − 1 day
        return resultFromSlices(
            splitCessationPeriods(periodFrom, periodTo, deriveCutoffMonthDayFromPeriodFrom(periodFrom)),
            'CESSATION',
        );
    }

    // —— Step 3: regular anniversary roll ——
    const slices = splitRegularPeriods(periodFrom, periodTo);
    return resultFromSlices(slices, 'REGULAR');
}

/** Fingerprint for clearing confirm when period changes. */
export function periodConfirmFingerprint(
    periodFrom?: string | null,
    periodTo?: string | null,
): string {
    return `${periodFrom || ''}|${periodTo || ''}`;
}

/**
 * List / title YOA — mirrors backend {@code AuditPeriodSplitUtils.formatYearOfAssessmentDisplay}.
 * SINGLE → single YOA; MULTI → "{yoaStart} ~ {yoaEnd}" (display-only; never persist).
 */
export function formatYearOfAssessmentDisplay(
    spanType: AuditSpanType | string | null | undefined,
    periodTo?: string | null,
    firstSlicePeriodTo?: string | null,
    firstSliceYoa?: string | null,
    lastSliceYoa?: string | null,
): string | null {
    const yoaEnd = (String(lastSliceYoa || '').trim() || computeYearOfAssessment(periodTo) || null);
    if (String(spanType || '').toUpperCase() !== 'MULTI') {
        return yoaEnd;
    }
    const yoaStart = (String(firstSliceYoa || '').trim()
        || computeYearOfAssessment(firstSlicePeriodTo)
        || null);
    if (!yoaStart || !yoaEnd) return yoaEnd ?? yoaStart;
    if (yoaStart === yoaEnd) return yoaEnd;
    return `${yoaStart} ~ ${yoaEnd}`;
}

type PeriodYoaSlice = {
    yearOfAssessment?: string | null;
    periodTo?: string | null;
} | null | undefined;

/**
 * Client / Adjust# / Audited column caption YOA for MULTI adjustment (BS or PL Total).
 * Prefers engagement span from API ({@code adjustRecord.yearOfAssessment}); else first~last slice.
 * Display-only — does not change amount / save / periodScope YOA.
 */
export function resolveAdjustmentColumnHeaderYoa(opts: {
    spanType?: string | null;
    periods?: PeriodYoaSlice[] | null;
    engagementYoa?: string | null;
    fallbackYoa?: string | null;
}): string | null {
    const span = String(opts.spanType || '').toUpperCase();
    const periods = (opts.periods || []).filter((p): p is NonNullable<PeriodYoaSlice> => !!p);
    const fallback = String(opts.fallbackYoa || '').trim() || null;
    if (span === 'MULTI' && periods.length >= 2) {
        const api = String(opts.engagementYoa || '').trim();
        if (api.includes(' ~ ')) return api;
        const first = periods[0];
        const last = periods[periods.length - 1];
        return formatYearOfAssessmentDisplay(
            'MULTI',
            last?.periodTo,
            first?.periodTo,
            first?.yearOfAssessment,
            last?.yearOfAssessment,
        ) || api || fallback;
    }
    return fallback;
}
